/*
Muestra la página de Preview de Cotizaciones,
y envía los datos al servidor.
Autor: Benjamín León
Fecha: 2024-09-20
*/
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import TemplateDto from '../interfaces/TemplateDto'
import Client from '../interfaces/Client'
import Vehicle from '../interfaces/Vehicle'
import { useAuth } from '../hooks/useAuth'
import useTemplateFetcher from '../hooks/useTemplateFetcher'
import { AppPath } from '../enums/AppPath'
import EstimateDto from '../interfaces/EstimateDto'
import { DEFAULT_TITLE_CONFIG } from '../utils/documentTitleUtils'
import EstimateDet from '../interfaces/EstimateDet'
import useEstimateCrea from '../hooks/useEstimateCrea'
import Deal from '../interfaces/Deal'

import DocConfirma from './DocConfirma'
import EstimateDetail from './EstimateDetail'
import DisplayError from './DisplayError'

const EstimatePreview: React.FC<{
  estimate: EstimateDto | null
  onGoBack: () => void
  onCreate?: (id: string) => void
  vehicle: Vehicle
  client: Client
  deal?: Deal
}> = ({ estimate, onGoBack, onCreate, vehicle, client, deal }) => {
  const { userData } = useAuth()
  const { errorTemplate, fetchTemplate } = useTemplateFetcher()
  const [template, setTemplate] = useState<TemplateDto | null>(null)
  const { estimateCrea, errorEstimateCrea, loading } = useEstimateCrea()
  const organizationId = userData?.organization?._id ?? ''

  useEffect(() => {
    const fetchDataAsync = async () => {
      const data = organizationId ? await fetchTemplate(organizationId, AppPath.Estimates) : null
      setTemplate(data)
    }
    fetchDataAsync()
  }, [organizationId])

  const navigate = useNavigate()
  // Handle Crear button click
  const handleCrearClick = async () => {
    if (!estimate) {
      return
    }
    const id = await estimateCrea(estimate)

    if (!id) {
      return
    }

    // Si nos va bien, volvemos a la ventana de detalle.
    if (onCreate) {
      onCreate(id)
    } else {
      const url = `/${AppPath.Estimates}/view/${id}`
      navigate(url)
    }
  }

  // Extract title configuration from template
  const titleConfig = template?.estimates?.title ?? { ...DEFAULT_TITLE_CONFIG }

  const est: EstimateDet = {
    titleConfig,
    organization: {
      pictures: template?.general.organization.pictures ?? { logotype: '', isotype: '' },
      nin: template?.general?.organization?.nin?.value ?? '',
      legalName: '',
      name: template?.general.organization.name.value ?? '',
      business:
        template?.general.organization.business.type !== '-'
          ? (template?.general.organization.business.value ?? '')
          : '',
      phone: template?.general.organization.phone.value ?? '',
      email: template?.general.organization.email?.value ?? '',
      domain: template?.general.organization.domain?.value ?? '',
      address: template?.general.organization.address?.value ?? '',
    },
    client: {
      nin: client?.nin ?? '',
      name: `${client?.firstname} ${client?.lastname}`,
      phone: client?.phone ?? '',
      email: client?.email ?? '',
      address: client?.address ?? '',
    },
    vehicle: {
      plate: vehicle?.plate ?? '',
      type: vehicle?.type ?? '',
      brand: vehicle?.brand ?? '',
      model: vehicle?.model ?? '',
      year: vehicle?.year ?? 0,
      color: vehicle?.color ?? '',
      odometer: vehicle?.odometer ?? 0,
      chassisId: vehicle?.chassisId ?? '',
      engineId: vehicle?.engineId ?? '',
      price: vehicle?.price ?? 0,
      version: vehicle?.version ?? '',
    },
    salePrice: estimate?.salePrice ?? 0,
    notes: estimate?.notes ?? '',
    number: estimate?.number ?? 0,
    expiresAt: estimate?.expiresAt ?? '',
    date: estimate?.date ?? '',
    discount: estimate?.discount ?? 0,
    discountHide: estimate?.discountHide ?? false,
    footer: template?.estimates.footer.value ?? '',
    profile_name: deal?.profile_user ?? userData?.user?.fullname ?? '',
  }

  return (
    <div className="p-2 max-w-3xl mx-auto">
      <div className="flex justify-center mt-1">
        {/* "Go Back" button */}
        <button className="bg-blue-500 text-white py-2 px-4 rounded mt-0 mb-2" onClick={onGoBack}>
          Volver a la cotización
        </button>
      </div>
      {/*   <EstimateDetail estimate={est} checked={checkedVer}/> */}
      <EstimateDetail estimate={est} />
      <DocConfirma docType={AppPath.Estimates} handleCrearClick={handleCrearClick} loading={loading} />
      {errorTemplate && <div>Error: {errorTemplate.message}</div>}
      {errorEstimateCrea && <DisplayError msg={errorEstimateCrea.message} />}
    </div>
  )
}

export default EstimatePreview
